import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from "@sentry/angular";
import type { ErrorEvent, EventHint } from "@sentry/core";
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';

/** Supabase Auth usa navigator.locks; timeout/race tra tab non sono bug applicativi. */
const SUPABASE_AUTH_LOCK_ERROR_PATTERNS = [
  /lock:sb-[\w-]*auth-token/i,
  /Navigator LockManager lock/i,
  /NavigatorLockAcquireTimeoutError/i,
  /Acquiring an exclusive Navigator LockManager lock/i,
] as const;

function isSupabaseAuthLockErrorMessage(message: string | undefined): boolean {
  if (!message) {
    return false;
  }
  return SUPABASE_AUTH_LOCK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function shouldIgnoreSupabaseAuthLockEvent(
  event: ErrorEvent,
  hint: EventHint,
): boolean {
  if (isSupabaseAuthLockErrorMessage(event.exception?.values?.[0]?.value)) {
    return true;
  }

  const original = hint.originalException;
  if (original instanceof Error && isSupabaseAuthLockErrorMessage(original.message)) {
    return true;
  }

  if (
    typeof original === "object" &&
    original !== null &&
    "message" in original &&
    typeof original.message === "string" &&
    isSupabaseAuthLockErrorMessage(original.message)
  ) {
    return true;
  }

  if (
    typeof original === "object" &&
    original !== null &&
    "isAcquireTimeout" in original &&
    (original as { isAcquireTimeout?: boolean }).isAcquireTimeout === true
  ) {
    return true;
  }

  return false;
}

Sentry.init({
  dsn: environment.dsn,
  environment: environment.applicationId,

  // Performance Monitoring & Tracing
  tracesSampleRate: environment.production ? 1.0 : 1.0, // 100% sia in prod che in dev
  enableLogs: true,
  sendDefaultPii: true,

  // Session Replay
  replaysSessionSampleRate: environment.production ? 1.0 : 0.1, // 100% delle sessioni normali
  replaysOnErrorSampleRate: environment.production ? 1.0 : 1.0, // 100% delle sessioni con errori

  // Browser Profiling (Client-side profiling)
  profileSessionSampleRate: environment.production ? 0.1 : 1.0, // 10% in prod, 100% in dev

  // Distributed Tracing - Propagazione trace tra frontend e backend
  tracePropagationTargets: [
    "localhost",              // Dev locale
    /^http:\/\/localhost/,    // Tutte le porte localhost
    /^http:\/\/127\.0\.0\.1/, // Localhost via IP
    /colibryx\.com/,          // Tutti i domini e sottodomini colibryx.com
  ],

  // Strict trace continuation - Continua solo trace della tua org
  strictTraceContinuation: true,

  // Release tracking
  release: `${environment.projectName}@${environment.version}`,

  ignoreErrors: [
    ...SUPABASE_AUTH_LOCK_ERROR_PATTERNS,
  ],

  // Callback per arricchire gli eventi con context
  beforeSend(event, hint) {
    if (shouldIgnoreSupabaseAuthLockEvent(event, hint)) {
      return null;
    }

    // Aggiungi info utente se disponibile
    const userId = localStorage.getItem('userId');
    const tenantId = localStorage.getItem('tenantId');

    if (userId) {
      event.user = {
        ...event.user,
        id: userId,
      };
    }

    if (tenantId) {
      event.tags = {
        ...event.tags,
        tenant_id: tenantId,
        project: environment.projectName,
      };
    }

    return event;
  },

  integrations: [
    // Invia i console.* al prodotto "Logs" di Sentry (richiede enableLogs: true).
    // Questo è ciò che popola la vista Logs con log/info/debug, non captureConsoleIntegration.
    Sentry.consoleLoggingIntegration({
      levels: ['error', 'warn', 'log', 'info', 'debug'],
    }),
    // Crea Issues dai console: tenuto stretto a error/warn per non inondare Sentry
    // (ogni console.log diventerebbe un issue → rumore e rischio rate-limit).
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn'],
    }),
    // Browser Tracing per performance monitoring
    Sentry.browserTracingIntegration({
      // Traccia fetch/XHR requests
      traceFetch: true,
      traceXHR: true,

      // Performance timing
      enableLongTask: true, // Traccia long tasks (>50ms)
      enableInp: true, // Interaction to Next Paint

      // Idle timeout per transactions
      idleTimeout: 1000,
      finalTimeout: 30000,
    }),

    // Browser Profiling per code-level insights
    Sentry.browserProfilingIntegration(),

    // User Feedback per raccogliere feedback dagli utenti
    Sentry.feedbackIntegration({
      colorScheme: "system",
      enableScreenshot: true,
      autoInject: false, // Non mostrare il widget automaticamente
      showBranding: false,

      // Campi visibili (nascosti perché i dati vengono passati automaticamente)
      showName: false,
      showEmail: false,

      // Usa i dati utente di Sentry automaticamente
      useSentryUser: {
        name: 'username', // Mappa il campo 'username' di Sentry.setUser() al campo 'name' del form
        email: 'email',   // Mappa il campo 'email' di Sentry.setUser() al campo 'email' del form
      },

      // Traduzioni italiane - Testi generali
      buttonLabel: "Segnala un problema",
      triggerLabel: "Segnala un problema",
      triggerAriaLabel: "Apri il modulo di feedback",
      formTitle: "Segnala un problema",

      // Traduzioni italiane - Bottoni
      submitButtonLabel: "Invia segnalazione",
      cancelButtonLabel: "Annulla",
      confirmButtonLabel: "Conferma",

      // Traduzioni italiane - Campi form
      nameLabel: "Nome",
      namePlaceholder: "Il tuo nome",
      emailLabel: "Email",
      emailPlaceholder: "la-tua-email@esempio.it",
      messageLabel: "Descrizione",
      messagePlaceholder: "Descrivi il problema riscontrato...",
      isRequiredLabel: "(obbligatorio)",

      // Traduzioni italiane - Screenshot
      addScreenshotButtonLabel: "Aggiungi screenshot",
      removeScreenshotButtonLabel: "Rimuovi screenshot",

      // Traduzioni italiane - Strumenti screenshot (v10.10.0+)
      highlightToolText: "Evidenzia",
      hideToolText: "Nascondi",
      removeHighlightText: "Rimuovi",

      // Traduzioni italiane - Messaggi di stato
      successMessageText: "Grazie per la tua segnalazione!",
    }),
  ],
});

// Carica Session Replay in modo lazy per ridurre il bundle size iniziale
Sentry.lazyLoadIntegration("replayIntegration").then(
  (replayIntegration) => {
    Sentry.addIntegration(replayIntegration({
      // Privacy - DISABILITATO per debugging completo
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: false,

      // Soglie alzate per UI molto dinamiche (planning Angular); trade-off: più CPU nel recording.
      mutationBreadcrumbLimit: 15000,
      mutationLimit: 20000,

      // Network Details - Cattura TUTTO il traffico di rete
      networkDetailAllowUrls: [
        window.location.origin, // Tutte le chiamate alla tua app
        /^https?:\/\//,         // Tutte le chiamate HTTP/HTTPS
      ],
      networkCaptureBodies: true,
      networkRequestHeaders: [
        'Content-Type',
        'Authorization',
        'Accept',
        'Cache-Control',
        'X-Requested-With',
      ],
      networkResponseHeaders: [
        'Content-Type',
        'Content-Length',
        'Cache-Control',
        'Referrer-Policy',
        'X-RateLimit-Remaining',
      ],

      // Session configuration
      stickySession: true,
      minReplayDuration: 1000 * 5,      // Invia replay dopo 5 secondi
      maxReplayDuration: 1000 * 60 * 60 * 24,    // Max 1 ora
    }));
  },
);

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));
